import {Injector} from "@angular/core"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {ShaderBallPostRenderStepInput, shaderBallPostRenderStepTask} from "@cm/job-nodes/asset-rendering"
import {CreateJobGraphData, JobNodes} from "@cm/job-nodes/job-nodes"
import {cmRenderTaskForPassNames} from "@cm/job-nodes/rendering"
import {MaterialReference, isMaterialInput, SceneNodes} from "@cm/template-nodes"
import {getLatestTemplateGraph, LegacyId} from "@common/helpers/rendering/material/material-assets-rendering/common"
import {combinedPassFromRenderTask} from "@common/helpers/rendering/rendering"
import {JobGraphMaterial} from "@common/models/material-assets-rendering/job-graph-material"
import {Settings} from "@common/models/settings/settings"
import {postProcessingGraph} from "@cm/image-processing/operations/render-post-processing"
import {ImageProcessingNodes} from "@cm/image-processing-nodes"
import {postProcessingSettingsToImageProcessingSettings} from "@app/template-editor/helpers/template-jobs"

export async function setupTemplateGraphForShaderBallRender(
    materialId: LegacyId,
    materialRevisionId: LegacyId | null,
    shaderBallTemplateId: LegacyId,
    injector: Injector,
    sceneManagerService: SceneManagerService,
): Promise<SceneNodes.SceneNode[]> {
    sceneManagerService.$templateGraph.set(await getLatestTemplateGraph(injector, shaderBallTemplateId))
    sceneManagerService.compileTemplate()
    await sceneManagerService.sync(true)

    const materialInputs = sceneManagerService.getDescriptors().filter((descriptor) => isMaterialInput(descriptor))
    if (materialInputs.length !== 1) throw new Error("Expected exactly one material input")

    sceneManagerService.setTemplateParameter(
        materialInputs[0].props.id,
        new MaterialReference({parameters: {name: "Material reference", materialId, materialRevisionId}}),
    )
    await sceneManagerService.sync(true)

    return sceneManagerService.$scene()
}

export function jobGraphFn_shaderBall(
    renderGraphDataObjectId: number,
    materialDetails: JobGraphMaterial,
    postProcessingSettings: SceneNodes.RenderPostProcessingSettings | undefined,
): CreateJobGraphData {
    // TODO: need to make these types more precise: JSON graph !== deduplicated DAG (!)
    const inputPlaceholder = {} as ImageProcessingNodes.ImageData
    const imageInput: ImageProcessingNodes.Input = {
        type: "input",
        image: inputPlaceholder,
    }
    const image: ImageProcessingNodes.Node = {
        type: "convert",
        dataType: "float32",
        channelLayout: "RGBA",
        input: postProcessingSettings
            ? postProcessingGraph(
                  {
                      combinedPass: imageInput,
                  },
                  postProcessingSettingsToImageProcessingSettings(postProcessingSettings),
              ).image
            : imageInput,
    }

    const renderTask = JobNodes.task(cmRenderTaskForPassNames("Combined"), {
        input: JobNodes.value({
            renderGraph: JobNodes.dataObjectReference(renderGraphDataObjectId),
            customerId: materialDetails.organization.legacyId,
            previewForProgressValue: [],
        }),
    })

    const postRenderTask = JobNodes.task(shaderBallPostRenderStepTask, {
        input: JobNodes.struct({
            renderImage: combinedPassFromRenderTask(renderTask),
            imageProcessingGraph: JobNodes.value({
                inputPlaceholder,
                output: image,
            }),
            materialId: JobNodes.value(materialDetails.legacyId),
            customerId: JobNodes.value(materialDetails.organization.legacyId),
            renderFileName: JobNodes.value("Thumbnail - " + materialDetails.name + ".exr"),
        }) as JobNodes.TypedDataNode<ShaderBallPostRenderStepInput>,
    })

    return JobNodes.jobGraph(postRenderTask, {
        progress: {
            type: "progressGroup",
            items: [
                {node: renderTask, factor: 18},
                {node: postRenderTask, factor: 1},
            ],
        },
        platformVersion: Settings.APP_VERSION,
    })
}
